// Vanuatu National Syllabus: Junior Secondary Mathematics Years 7-10
// Based on official syllabus documents

export const juniorMathematicsCurriculum = {
  year7: {
    title: "Year 7 Mathematics",
    description: "Foundation mathematical concepts covering number operations, algebraic thinking, measurement, geometry, and statistics",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Number",
        hours: 26,
        percentage: 20,
        description: "Apply effective strategies for numerical calculation and problem-solving",
        topics: [
          {
            id: "7MAT1.1",
            name: "Number and Operations",
            description: "Operations with whole numbers, integers, and decimals",
            weeks: "1-4",
            learningOutcomes: [
              "Calculate answers to real-life problems involving positive and negative numbers",
              "Calculate with decimals up to two decimal places",
              "Round numbers to a given number of decimal places or significant figures",
              "Use estimation to verify when answers are reasonable",
              "Apply order of operations (BODMAS/BIDMAS)",
              "Use index notation for squares and cubes",
              "Find square roots of perfect squares"
            ]
          },
          {
            id: "7MAT1.2",
            name: "Fractions, Percentages, Ratio and Proportion",
            description: "Working with fractions, percentages, ratios and proportions",
            weeks: "5-8",
            learningOutcomes: [
              "Compare and order fractions, decimals and percentages",
              "Convert between fractions, decimals and percentages",
              "Add, subtract, multiply and divide fractions",
              "Calculate percentages of quantities",
              "Find percentage increase and decrease",
              "Simplify and use ratios",
              "Solve problems involving direct proportion"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Algebra",
        hours: 33,
        percentage: 25,
        description: "Use algebraic expressions to represent and solve problems",
        topics: [
          {
            id: "7MAT2.1",
            name: "Expressions",
            description: "Algebraic expressions and simplification",
            weeks: "9-11",
            learningOutcomes: [
              "Use letters to represent unknown numbers",
              "Write expressions from word problems",
              "Simplify expressions by collecting like terms",
              "Substitute values into expressions",
              "Expand single brackets",
              "Understand and use algebraic conventions"
            ]
          },
          {
            id: "7MAT2.2",
            name: "Equations",
            description: "Solving linear equations",
            weeks: "12-14",
            learningOutcomes: [
              "Solve one-step and two-step linear equations",
              "Solve equations with unknown on both sides",
              "Form equations from word problems",
              "Check solutions by substitution"
            ]
          },
          {
            id: "7MAT2.3",
            name: "Coordinate Geometry",
            description: "Coordinates in four quadrants",
            weeks: "15-16",
            learningOutcomes: [
              "Read and plot coordinates in all four quadrants",
              "Identify coordinates of points on a grid",
              "Draw and interpret simple graphs",
              "Find coordinates of midpoints"
            ]
          },
          {
            id: "7MAT2.4",
            name: "Sequences",
            description: "Number patterns and sequences",
            weeks: "17-18",
            learningOutcomes: [
              "Continue sequences using term-to-term rules",
              "Find missing terms in sequences",
              "Describe patterns in sequences",
              "Generate sequences from given rules"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Measurement",
        hours: 26,
        percentage: 20,
        description: "Estimate and calculate measurements in various contexts",
        topics: [
          {
            id: "7MAT3.1",
            name: "Units of Measurement",
            description: "Standard units and conversions",
            weeks: "19-21",
            learningOutcomes: [
              "Use appropriate units for length, mass, capacity and time",
              "Convert between metric units",
              "Estimate measurements in everyday contexts",
              "Read scales on measuring instruments",
              "Calculate with time including the 24-hour clock"
            ]
          },
          {
            id: "7MAT3.2",
            name: "Perimeter, Area and Volume",
            description: "Calculating perimeter, area and volume",
            weeks: "22-25",
            learningOutcomes: [
              "Calculate perimeter of polygons",
              "Calculate area of rectangles, triangles and parallelograms",
              "Calculate area of compound shapes",
              "Calculate volume of cubes and cuboids",
              "Use appropriate units for area and volume"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Geometry",
        hours: 20,
        percentage: 15,
        description: "Understand geometric reasoning and spatial features",
        topics: [
          {
            id: "7MAT4.1",
            name: "Angles and Lines",
            description: "Properties of angles and parallel lines",
            weeks: "26-28",
            learningOutcomes: [
              "Identify acute, obtuse, reflex and right angles",
              "Calculate angles on a straight line and at a point",
              "Calculate vertically opposite angles",
              "Identify parallel and perpendicular lines",
              "Calculate angles in parallel lines (corresponding, alternate, co-interior)"
            ]
          },
          {
            id: "7MAT4.2",
            name: "Shapes and Symmetry",
            description: "2D and 3D shapes, symmetry and transformations",
            weeks: "29-32",
            learningOutcomes: [
              "Identify and classify 2D shapes by properties",
              "Identify and describe 3D shapes",
              "Identify lines of symmetry",
              "Identify rotational symmetry",
              "Perform reflections, rotations and translations",
              "Draw nets of 3D shapes"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Probability and Statistics",
        hours: 27,
        percentage: 20,
        description: "Collect, organize, represent and analyze data; use probabilities",
        topics: [
          {
            id: "7MAT5.1",
            name: "Probability",
            description: "Basic probability concepts",
            weeks: "33-35",
            learningOutcomes: [
              "Describe probability using words (impossible, unlikely, likely, certain)",
              "Use the probability scale from 0 to 1",
              "Calculate theoretical probability of single events",
              "List outcomes systematically",
              "Conduct simple probability experiments"
            ]
          },
          {
            id: "7MAT5.2",
            name: "Data",
            description: "Data collection, representation and interpretation",
            weeks: "36-40",
            learningOutcomes: [
              "Collect data through surveys and experiments",
              "Organize data in frequency tables",
              "Draw and interpret bar charts, pictograms and pie charts",
              "Calculate mean, median, mode and range",
              "Draw conclusions from data",
              "Compare data sets using averages"
            ]
          }
        ]
      }
    ]
  },
  year8: {
    title: "Year 8 Mathematics",
    description: "Building on Year 7 foundations with significant figures, indices, mixed numbers, expanding and factorising expressions, coordinate geometry, circles, Pythagoras' Theorem, and probability with replacement",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Number",
        hours: 26,
        percentage: 20,
        description: "Apply effective strategies for numerical calculation and problem-solving",
        topics: [
          {
            id: "8MAT1.1",
            name: "Number and Operations",
            description: "Estimate and calculate answers to real-life problems involving positive and negative numbers with up to two decimal places, irrational numbers and indices; and verify when answers are reasonable",
            weeks: "1-4",
            learningOutcomes: [
              "Round numbers of any size to one, two or three significant figures",
              "Use significant figures to estimate answers to calculations and verify when answers are reasonable",
              "Explain the differences between recurring and non-terminating decimals and irrational numbers",
              "Calculate numerical expressions involving squares, cubes, square roots and cube roots",
              "Use index notation to simplify and calculate numeric expressions with positive and zero integer indices",
              "Add, subtract, multiply and divide positive and negative numbers with up to two decimal places as required to solve real-life problems"
            ]
          },
          {
            id: "8MAT1.2",
            name: "Fractions, Percentages, Ratio and Proportion",
            description: "Apply mixed numbers, percentages, direct proportion and ratio as required to solve real-life problems",
            weeks: "5-8",
            learningOutcomes: [
              "Add, subtract, multiply and divide mixed numbers",
              "Calculate fractions of amounts",
              "Find the reciprocal of a fraction",
              "Recognise when a graph represents direct proportion",
              "Calculate missing quantities for two items in direct proportion",
              "Solve problems involving profit and loss by calculating percentage change"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Algebra",
        hours: 33,
        percentage: 25,
        description: "Recognise, describe and represent patterns and relationships; and apply algebraic and geometric skills and techniques to model and solve everyday problems",
        topics: [
          {
            id: "8MAT2.1",
            name: "Expressions",
            description: "Expand and factorise expressions with common numerical and algebraic factors; and monic quadratic expressions",
            weeks: "9-11",
            learningOutcomes: [
              "Expand algebraic expressions involving two sets of brackets",
              "Factorise algebraic expressions with a numerical or algebraic factor or both",
              "Factorise monic quadratic expressions"
            ]
          },
          {
            id: "8MAT2.2",
            name: "Equations and Inequalities",
            description: "Solve problems involving linear equations, including when the unknown appears on each side both algebraically and graphically; and solve simple linear inequalities",
            weeks: "12-15",
            learningOutcomes: [
              "Write and solve linear equations with the unknown on both sides",
              "Solve linear equations including brackets and fractional coefficients",
              "Solve simple linear inequalities and represent the solution on a number line",
              "Verify solutions using substitution"
            ]
          },
          {
            id: "8MAT2.3",
            name: "Coordinate Geometry",
            description: "Apply algebraic and geometric skills to solve problems involving line segments and linear graphs; and sketch and accurately plot linear graphs",
            weeks: "16-20",
            learningOutcomes: [
              "Calculate the midpoint, length and gradient of a line segment",
              "Accurately plot linear and quadratic relationships from equations, tables of values and real-life contexts",
              "Recognise and find the equations of straight-line graphs parallel to the axes",
              "Recognise graphs of y=x and y=-x",
              "Identify the rule for a linear graph from the gradient and intercept, and the gradient and intercept from an equation",
              "Solve linear equations using graphical techniques",
              "Calculate the gradient and y-intercept from a linear graph",
              "Sketch graphs using the y-intercept and gradient"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Measurement",
        hours: 26,
        percentage: 20,
        description: "Demonstrate skills of estimation, measurement and calculation in a range of contexts, using appropriate units and formulae; and solve real-life problems involving measurement",
        topics: [
          {
            id: "8MAT3.1",
            name: "Perimeter and Area",
            description: "Use formulae involving pi and logical approaches to solve real-life problems involving circumference and area of full circles and parts of circles",
            weeks: "21-25",
            learningOutcomes: [
              "Discover pi as the ratio of the circumference of a circle to the diameter",
              "Estimate and calculate circumference of a circle and lengths of arcs, given the radius or diameter",
              "Estimate and calculate areas of circles and sectors, given the radius or diameter",
              "Calculate the diameter or radius of a circle given the circumference or area",
              "Use formulae to solve problems where the unknown is not the subject of the formula"
            ]
          },
          {
            id: "8MAT3.2",
            name: "Volume and Surface Area",
            description: "Use formulae and logical approaches to solve real-life problems involving surface area and volume of prisms and cylinders",
            weeks: "26-29",
            learningOutcomes: [
              "Calculate the surface area of cubes, cuboids, prisms and cylinders",
              "Calculate the surface area of three-dimensional objects composed of prisms",
              "Calculate the volume of prisms, including cylinders",
              "Convert between metric measures for area and for volume",
              "Calculate the capacity of three-dimensional containers"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Geometry",
        hours: 20,
        percentage: 15,
        description: "Demonstrate understanding of geometric reasoning and spatial awareness and analyse mathematically the spatial features of objects, turns and movements; and solve problems requiring geometric reasoning",
        topics: [
          {
            id: "8MAT4.1",
            name: "Shapes",
            description: "Draw lines in and around shapes according to a set of rules; and apply these loci to solve real-life problems",
            weeks: "30-31",
            learningOutcomes: [
              "Construct perpendicular bisectors of line segments and angle bisectors using a ruler and compass",
              "Construct angles that are multiples of 30 or 45 degrees using a ruler and compasses",
              "Use loci to explain constructions"
            ]
          },
          {
            id: "8MAT4.2",
            name: "Angles",
            description: "Apply knowledge of angles, including interior and exterior angles in polygons, to solve problems",
            weeks: "32-34",
            learningOutcomes: [
              "Justify the rule for the sum of angles in a triangle and quadrilateral",
              "Find and justify the rule for the angle sum of interior and exterior angles of any convex polygon",
              "Calculate the direction of one point relative to another using bearings",
              "Find unknown angles in triangles and quadrilaterals",
              "Find unknown interior and exterior angles of a polygon"
            ]
          },
          {
            id: "8MAT4.3",
            name: "Triangle Geometry",
            description: "Solve two-dimensional, real-life problems involving Pythagoras' Theorem",
            weeks: "35-36",
            learningOutcomes: [
              "Use Pythagoras' Theorem to find the length of an unknown side of a right-angled triangle",
              "Determine whether a triangle is right-angled given the lengths of three sides",
              "Solve problems involving unknown sides of shapes involving right-angled triangles"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Probability and Statistics",
        hours: 27,
        percentage: 20,
        description: "Collect, organise, represent and analyse data; and use probabilities to draw conclusions and make predictions from real-life scenarios",
        topics: [
          {
            id: "8MAT5.1",
            name: "Probability",
            description: "Represent sample space and calculate probabilities for two events with or without replacement; and compare experimental outcomes with theoretical predictions in real-life problems",
            weeks: "37-38",
            learningOutcomes: [
              "Represent the sample space using lists, tables or tree diagrams",
              "Use tree diagrams to identify the outcomes and probability of two events with or without replacement",
              "Calculate expected outcomes to decide if a game is fair"
            ]
          },
          {
            id: "8MAT5.2",
            name: "Data",
            description: "Identify and justify appropriate sampling techniques for primary and secondary data sources; and draw and interpret statistical diagrams for real-life situations with relevant conclusions",
            weeks: "39-40",
            learningOutcomes: [
              "Identify appropriate sampling techniques for primary and secondary data sources",
              "Estimate the mean from grouped frequency tables and identify the modal group",
              "Calculate the mode, median, mean and range for frequency tables and use them to compare data sets",
              "Draw, use and interpret scatter plots, pie charts, and stem and leaf plots",
              "Draw a line of best fit on a scatter plot and use it to predict results",
              "Explain the difference between correlation and causation and justify when a graph represents either or both"
            ]
          }
        ]
      }
    ]
  },
  year9: {
    title: "Year 9 Mathematics",
    description: "Consolidating mathematical concepts with introduction to quadratics, trigonometry, advanced statistics and coordinate geometry based on the Vanuatu National Syllabus",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40,
      assessmentTasks: [
        { name: "Teacher Design Task 1 (TDT1)", type: "TDT" },
        { name: "Teacher Design Task 2 (TDT2)", type: "TDT" },
        { name: "Common Assessment Task (CAT)", type: "CAT" }
      ]
    },
    strands: [
      {
        id: "strand1",
        name: "Number",
        hours: 26,
        percentage: 20,
        description: "Apply effective strategies for numerical calculation and problem-solving",
        topics: [
          {
            id: "9MAT1.1",
            name: "Number and Operations",
            description: "Index laws, scientific notation, and number operations",
            weeks: "1-4",
            learningOutcomes: [
              "Use index laws for multiplication, division, power of a power",
              "Apply negative and zero indices",
              "Use fractional indices to write roots",
              "Convert between ordinary numbers and scientific notation",
              "Perform operations with numbers in scientific notation",
              "Apply index laws to simplify algebraic expressions",
              "Use estimation to verify reasonableness of answers"
            ]
          },
          {
            id: "9MAT1.2",
            name: "Fractions, Percentages, Ratio and Proportion",
            description: "Complex applications of proportional reasoning including direct and inverse proportion",
            weeks: "5-8",
            learningOutcomes: [
              "Convert terminating and recurring decimals to fractions",
              "Solve complex percentage problems including reverse percentage",
              "Calculate compound interest and depreciation",
              "Divide quantities in given ratios including three-part ratios",
              "Apply direct proportion using the constant k",
              "Apply inverse proportion and recognise the relationship",
              "Solve real-life proportion problems"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Algebra",
        hours: 33,
        percentage: 25,
        description: "Expand algebraic skills including quadratics, simultaneous equations and coordinate geometry",
        topics: [
          {
            id: "9MAT2.1",
            name: "Expressions",
            description: "Expanding, factorising and simplifying algebraic expressions including quadratics",
            weeks: "9-11",
            learningOutcomes: [
              "Expand products of two binomials",
              "Factorise quadratic expressions of the form x^2 + bx + c",
              "Factorise difference of two squares",
              "Simplify algebraic fractions",
              "Add, subtract, multiply and divide algebraic fractions",
              "Apply index rules to algebraic expressions"
            ]
          },
          {
            id: "9MAT2.2",
            name: "Formulae",
            description: "Deriving and rearranging formulae including change of subject",
            weeks: "12-13",
            learningOutcomes: [
              "Change the subject of a formula involving powers and roots",
              "Derive formulae from complex situations",
              "Substitute into formulae with multiple variables",
              "Change the subject when it appears twice in a formula"
            ]
          },
          {
            id: "9MAT2.3",
            name: "Equations",
            description: "Quadratic and simultaneous equations",
            weeks: "14-17",
            learningOutcomes: [
              "Solve quadratic equations by factorisation",
              "Solve quadratic equations using the quadratic formula",
              "Solve simultaneous equations by elimination",
              "Solve simultaneous equations by substitution",
              "Solve simultaneous equations graphically",
              "Form and solve equations from word problems"
            ]
          },
          {
            id: "9MAT2.4",
            name: "Coordinate Geometry",
            description: "Gradient, midpoint, distance and equation of a straight line",
            weeks: "18-20",
            learningOutcomes: [
              "Calculate gradient of a line from two points",
              "Find the midpoint of a line segment",
              "Calculate the distance between two points",
              "Write the equation of a straight line (y = mx + c)",
              "Determine parallel and perpendicular line relationships",
              "Plot and sketch quadratic graphs and identify key features"
            ]
          },
          {
            id: "9MAT2.5",
            name: "Sequences",
            description: "Linear and quadratic sequences, nth term",
            weeks: "21-22",
            learningOutcomes: [
              "Find the nth term of linear sequences",
              "Find the nth term of quadratic sequences",
              "Generate sequences from nth term rules",
              "Identify Fibonacci-type and geometric sequences"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Measurement",
        hours: 26,
        percentage: 20,
        description: "Calculate measurements involving circles, arcs, sectors, surface area and volume",
        topics: [
          {
            id: "9MAT3.1",
            name: "Metric System and Conversions",
            description: "Unit conversions and compound measures",
            weeks: "23-24",
            learningOutcomes: [
              "Convert between metric units of length, area and volume",
              "Calculate speed, distance and time",
              "Calculate density and pressure",
              "Apply appropriate degrees of accuracy",
              "Understand and use error intervals"
            ]
          },
          {
            id: "9MAT3.2",
            name: "Circles, Arcs and Sectors",
            description: "Arc length, sector area, and circle geometry",
            weeks: "25-27",
            learningOutcomes: [
              "Calculate circumference and area of circles",
              "Calculate arc length and sector area",
              "Calculate perimeter and area of compound shapes involving circles",
              "Solve problems involving semicircles and quarter circles"
            ]
          },
          {
            id: "9MAT3.3",
            name: "Surface Area and Volume",
            description: "3D shapes including pyramids, cones and spheres",
            weeks: "28-29",
            learningOutcomes: [
              "Calculate surface area and volume of prisms and cylinders",
              "Calculate volume of pyramids and cones",
              "Calculate volume and surface area of spheres",
              "Apply length, area and volume scale factors to similar shapes"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Geometry",
        hours: 20,
        percentage: 15,
        description: "Apply transformations, Pythagoras' theorem and trigonometry",
        topics: [
          {
            id: "9MAT4.1",
            name: "Transformations",
            description: "Reflection, rotation, translation and enlargement",
            weeks: "30-31",
            learningOutcomes: [
              "Describe and perform reflections, rotations and translations",
              "Describe and perform enlargements with positive and negative scale factors",
              "Describe combined transformations",
              "Use vectors to describe translations"
            ]
          },
          {
            id: "9MAT4.2",
            name: "Pythagoras and Trigonometry",
            description: "Pythagoras' theorem and trigonometric ratios",
            weeks: "32-35",
            learningOutcomes: [
              "Apply Pythagoras' theorem to find sides in right-angled triangles",
              "Apply Pythagoras' theorem in 3D problems",
              "Define sin, cos and tan ratios",
              "Use trigonometric ratios to find missing sides",
              "Use inverse trigonometric ratios to find angles",
              "Calculate area of triangle using 1/2 ab sin C",
              "Apply bearings using trigonometry"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Probability and Statistics",
        hours: 27,
        percentage: 20,
        description: "Advanced probability, cumulative frequency and data representation",
        topics: [
          {
            id: "9MAT5.1",
            name: "Probability",
            description: "Two-way tables, Venn diagrams, tree diagrams and combined events",
            weeks: "36-37",
            learningOutcomes: [
              "Use two-way tables and Venn diagrams to record and calculate probabilities",
              "Use tree diagrams for sequential events",
              "Calculate probabilities with and without replacement",
              "Apply the addition and multiplication rules",
              "Solve probability problems systematically"
            ]
          },
          {
            id: "9MAT5.2",
            name: "Data",
            description: "Grouped data, cumulative frequency, box plots and quartiles",
            weeks: "38-40",
            learningOutcomes: [
              "Calculate statistics from grouped frequency tables",
              "Construct cumulative frequency graphs",
              "Find median and quartiles from cumulative frequency graphs",
              "Construct and interpret box plots",
              "Compare distributions using box plots and statistical measures",
              "Identify skewness in data distributions"
            ]
          }
        ]
      }
    ]
  },
  year10: {
    title: "Year 10 Mathematics",
    description: "Preparing for senior secondary with surds, advanced algebra, trigonometry (sine/cosine rules) and comprehensive statistics based on the Vanuatu National Syllabus",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      externalExam: "National examination set by EAU/Ministry of Education",
      assessmentTasks: [
        { name: "Teacher Design Task 1 (TDT1)", type: "TDT" },
        { name: "Teacher Design Task 2 (TDT2)", type: "TDT" },
        { name: "Common Assessment Task (CAT)", type: "CAT" }
      ]
    },
    strands: [
      {
        id: "strand1",
        name: "Number",
        hours: 17,
        percentage: 13,
        description: "Surds and advanced proportional reasoning with multipliers",
        topics: [
          {
            id: "10MAT1.1",
            name: "Number and Operations (Surds)",
            description: "Simplifying numerical expressions involving surds and using surds in calculations",
            weeks: "1-4",
            learningOutcomes: [
              "Define surds; identify if a number is a surd",
              "Identify like and unlike surds",
              "Find square roots; classify numbers as whole, decimal, fraction or surd",
              "Simplify surds (e.g. sqrt(12), sqrt(18), sqrt(50))",
              "Simplify expressions with like-surds",
              "Use rules sqrt(a) x sqrt(b) = sqrt(ab) and sqrt(a)/sqrt(b) = sqrt(a/b)",
              "Solve algebraic problems requiring simplification of surds",
              "Rationalise the denominator of a fraction",
              "Solve word problems that include surds in measurements"
            ]
          },
          {
            id: "10MAT1.2",
            name: "Fractions, Percentages, Ratio and Proportion",
            description: "Using multipliers to solve real-life problems involving direct/inverse proportion and percentages",
            weeks: "5-7",
            learningOutcomes: [
              "State multipliers for common percentages; convert percentages to multipliers",
              "Apply multipliers for percentage change, reverse percentages, repeated percentage change",
              "Define direct and inverse proportion",
              "Set up equations for direct and inverse proportion",
              "Solve problems involving the constant of proportionality",
              "Calculate real-life percentage changes using multipliers",
              "Solve real-life problems involving repeated percentage change"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Algebra",
        hours: 63,
        percentage: 48,
        description: "Master quadratics, algebraic proof, simultaneous equations, coordinate geometry and sequences",
        topics: [
          {
            id: "10MAT2.1",
            name: "Expressions",
            description: "Manipulating quadratics, algebraic fractions, and algebraic proof",
            weeks: "8-11",
            learningOutcomes: [
              "Simplify and expand quadratic expressions",
              "Complete the square for a quadratic expression",
              "Simplify fractions with linear/quadratic denominators",
              "Add, subtract, multiply and divide algebraic fractions",
              "State the difference between a proof and a calculation",
              "Explain how to use mathematical proofs",
              "Discuss a logical argument using algebra"
            ]
          },
          {
            id: "10MAT2.2",
            name: "Formulae",
            description: "Changing the subject of a formula where the subject appears more than once",
            weeks: "12-13",
            learningOutcomes: [
              "Identify common formulae and key parts (subject, constant, variables)",
              "Explain difference between equations, formulae, functions, identities",
              "Change the subject where power/root of new subject appears",
              "Change the subject where new subject appears twice",
              "Solve real-life problems requiring change of subject before substituting"
            ]
          },
          {
            id: "10MAT2.3",
            name: "Equations",
            description: "Quadratic equations, inequalities, and simultaneous equations (linear-quadratic)",
            weeks: "14-18",
            learningOutcomes: [
              "Solve quadratic equations by factorising",
              "Solve quadratic equations by completing the square",
              "Solve quadratic equations using the quadratic formula",
              "Solve simultaneous equations where one equation is quadratic",
              "Analyse word problems for quadratic and linear equations",
              "Solve equations involving algebraic fractions",
              "Solve inequalities; show on number line and set-builder notation",
              "Solve two linear inequalities",
              "Use graphical/algebraic methods to find intersection of two linear equations"
            ]
          },
          {
            id: "10MAT2.4",
            name: "Coordinate Geometry",
            description: "Quadratic graphs, parallel/perpendicular lines, inequalities, and circles",
            weeks: "19-23",
            learningOutcomes: [
              "Calculate distance, midpoint and gradient using formulae",
              "Find equation of a line through a point with given gradient",
              "Find equations of parallel and perpendicular lines",
              "Classify lines as parallel/perpendicular from gradients",
              "Solve geometric problems using coordinates",
              "Shade regions satisfying linear inequalities",
              "Sketch quadratic graphs: y = (x-p)^2 + q and y = (x+a)(x+b)",
              "List features of quadratic graphs (max/min, symmetry, coefficient sign)",
              "Solve quadratic inequalities graphically",
              "State equation of circle with centre at origin (x^2 + y^2 = r^2)",
              "Draw graph of circle from its equation"
            ]
          },
          {
            id: "10MAT2.5",
            name: "Sequences",
            description: "Arithmetic sequences and their applications",
            weeks: "24-25",
            learningOutcomes: [
              "Identify number sequences; find common ratio",
              "Find the general (nth) term of an arithmetic sequence",
              "Prove that a situation is an arithmetic sequence",
              "Apply arithmetic sequences to solve worded problems"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Measurement",
        hours: 9,
        percentage: 6,
        description: "Perimeter, area and volume of 2D and 3D shapes",
        topics: [
          {
            id: "10MAT3.1",
            name: "Perimeter, Area and Volume",
            description: "Solve problems involving 2D perimeter/area and 3D volume/surface area",
            weeks: "26-28",
            learningOutcomes: [
              "Calculate perimeter and area of 2D shapes",
              "Calculate volume and surface area of cubes/rectangular prisms",
              "Calculate volume and surface area of cylinders",
              "Calculate volume of pyramids (V = 1/3 Ah)",
              "Calculate volume and surface area of cones (V = 1/3 pi r^2 h)",
              "Solve problems involving both surface area and volume",
              "Solve word problems using real-life models (e.g. copra dryer)"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Geometry",
        hours: 21,
        percentage: 16,
        description: "Geometric proof, transformations, and trigonometry including sine and cosine rules",
        topics: [
          {
            id: "10MAT4.1",
            name: "Shapes and Geometric Proof",
            description: "Angle properties of polygons and congruence proofs",
            weeks: "29-30",
            learningOutcomes: [
              "Calculate angle sum in regular polygons",
              "Prove angles in triangle sum to 180 degrees",
              "Prove angles in quadrilateral sum to 360 degrees",
              "Prove exterior angle equals sum of opposite interior angles",
              "Prove shapes are congruent (SSS/SAS/ASA/RHS)",
              "Formulate proofs involving congruent triangles"
            ]
          },
          {
            id: "10MAT4.2",
            name: "Transformations",
            description: "Combined transformations and 3D similar figures",
            weeks: "31-32",
            learningOutcomes: [
              "Define and identify four types of transformations",
              "Perform translations, reflections, rotations and enlargements",
              "Describe a single transformation given object and image",
              "Calculate perimeter/area of enlarged shapes using scale factors",
              "Solve problems involving composition of three or more transformations",
              "Solve problems involving 3D similar figures"
            ]
          },
          {
            id: "10MAT4.3",
            name: "Triangle Geometry (Trigonometry)",
            description: "Trigonometry for right-angled and non-right-angled triangles including sine/cosine rules",
            weeks: "33-36",
            learningOutcomes: [
              "Calculate missing sides/angles using SOH/CAH/TOA",
              "Solve problems combining trigonometry and Pythagoras' theorem",
              "Solve 3D trigonometry problems",
              "Solve problems involving angles of elevation and depression",
              "Apply sine rule to find unknown sides and angles",
              "Apply cosine rule to find unknown sides and angles",
              "Solve 2D problems using sine and cosine rules",
              "Use trigonometry within 2D coordinate geometry"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Probability and Statistics",
        hours: 22,
        percentage: 17,
        description: "Probability with set notation and statistical analysis including standard deviation",
        topics: [
          {
            id: "10MAT5.1",
            name: "Probability",
            description: "Sample space, set notation and probability methods",
            weeks: "37-38",
            learningOutcomes: [
              "Define event, sample space, outcome",
              "List sample space of events",
              "Analyse appropriate ways to display sample space",
              "Interpret information given in set notation",
              "Use set notation to answer probability questions"
            ]
          },
          {
            id: "10MAT5.2",
            name: "Data",
            description: "Graphs for continuous data, measures of centre and spread including standard deviation",
            weeks: "39-40",
            learningOutcomes: [
              "Identify data as discrete or continuous",
              "Organise data into frequency tables; draw bar graphs and histograms",
              "Construct frequency polygons for data comparison",
              "Calculate mean, median, mode",
              "Calculate standard deviation",
              "Find range and interquartile range",
              "Describe frequency density for histograms",
              "Construct and interpret histograms",
              "Use measures of centre and spread to compare distributions"
            ]
          }
        ]
      }
    ]
  }
};

export default juniorMathematicsCurriculum;
